import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Tooltip,
  Switch,
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  DeleteOutline as DeleteIcon,
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon,
  Science as ProtocolIcon,
  RadioButtonUnchecked as PendingIcon,
  GraphicEq as ListeningIcon,
} from '@mui/icons-material';
import {
  getProtocolConfig, getPublished, attachProtocolToSession,
  getSessionChecklist, markChecklistItem, observeChecklist,
} from '../api/protocols';
import VitalsSummary from '../components/VitalsSummary';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const AI_ASSIST_KEY = 'scribelive.protocolChecklistAiAssist';

export default function ScribeSession() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [patient, setPatient] = useState(null);
  const [openStopDialog, setOpenStopDialog] = useState(false);
  const [micError, setMicError] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // 'uploading' | 'done' | 'error'
  // A recording that has ended — this time, or on an earlier visit to this screen — and
  // is waiting to become a note. Once there is one, Start Recording would only replace it.
  const [recordingDone, setRecordingDone] = useState(false);
  const [openDiscardDialog, setOpenDiscardDialog] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [publishedProtocols, setPublishedProtocols] = useState([]);
  const [selectedProtocol, setSelectedProtocol] = useState('');
  // Point-of-care checklist: the protocol's questions, ticked green as they're
  // asked and answered. `liveStatus` describes the listener that does the ticking:
  // idle (not recording) | listening | complete (nothing left) | paused (AI check
  // unavailable — recording carries on regardless).
  const [checklist, setChecklist] = useState([]);
  const [liveStatus, setLiveStatus] = useState('idle');
  // Whether the AI listens along and ticks questions off, or the provider does it
  // by hand. It costs real AI credits per visit, so it's the provider's call and
  // their choice is remembered on this device.
  const [aiAssist, setAiAssist] = useState(() => localStorage.getItem(AI_ASSIST_KEY) !== 'off');
  const [aiChecks, setAiChecks] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  // The finished recording, kept so a failed upload can be retried rather than lost.
  const pendingAudioRef = useRef(null);
  // What to do once the recording has uploaded (Generate Note), and whether to upload
  // at all (not when the visit is being discarded).
  const afterUploadRef = useRef(null);
  const skipUploadRef = useRef(false);
  const timerRef = useRef(null);
  // Live-listen loop. It records its own short clips from a clone of the mic
  // stream, so the primary recording above is never interrupted by it.
  const liveStreamRef = useRef(null);
  const liveRecorderRef = useRef(null);
  const liveTimerRef = useRef(null);
  const liveActiveRef = useRef(false);
  const liveFlushRef = useRef(false);
  const liveFailuresRef = useRef(0);
  const lastClipTextRef = useRef('');
  const chunkSecondsRef = useRef(15);
  const checklistRef = useRef([]);
  checklistRef.current = checklist;
  const aiAssistRef = useRef(aiAssist);
  aiAssistRef.current = aiAssist;

  const pendingCount = checklist.filter((i) => !i.covered).length;
  const coveredCount = checklist.length - pendingCount;

  // A visit is created on the server the moment this screen mounts, so the mount must
  // happen exactly once. React's StrictMode double-invokes effects in development, and
  // any remount would do the same in production — either way a second, orphaned visit
  // gets started, and it sits there holding a recording nobody will ever finish.
  const startedRef = useRef(false);

  const startSession = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    try {
      const response = await axios.post(`${API_URL}/api/v1/clinical/session/start`, {
        patient_id: patientId
      });
      setSessionId(response.data.session_id);
      // Back to a visit that was recorded but never written up: its recording is on
      // the server, ready to become a note.
      if (response.data.has_audio) {
        setRecordingDone(true);
        setUploadStatus('done');
      }

      const patientRes = await axios.get(`${API_URL}/api/v1/clinical/patients/${patientId}`);
      setPatient(patientRes.data.patient);
    } catch (error) {
      // Say WHY — "no AI sessions left" or "not your patient" — rather than a bare failure.
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to start session', { duration: 7000 });
      navigate('/patients');
    }
  }, [patientId, navigate]);

  // Tear down the live listener. `flush` keeps the clip in progress: when the
  // provider stops recording we still want the last few seconds checked, but
  // when the page unmounts the visit is being abandoned and we send nothing.
  const stopLiveListening = useCallback((flush = false) => {
    liveActiveRef.current = false;
    liveFlushRef.current = !!flush;
    if (liveTimerRef.current) {
      clearTimeout(liveTimerRef.current);
      liveTimerRef.current = null;
    }
    const stream = liveStreamRef.current;
    liveStreamRef.current = null;
    const releaseMic = () => {
      try { stream?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    };
    try {
      const rec = liveRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        // Release the cloned mic only once the recorder has flushed its final
        // blob — pulling the track out from under it truncates that clip.
        rec.addEventListener('stop', releaseMic, { once: true });
        rec.stop();
        return;
      }
    } catch (_) {}
    releaseMic();
  }, []);

  useEffect(() => {
    startSession();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      // Safety: if the user navigates away mid-recording, stop the recorder
      // and release the microphone so it isn't left open.
      try {
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== 'inactive') mr.stop();
        mr?.stream?.getTracks().forEach((t) => t.stop());
      } catch (_) {}
      stopLiveListening();
    };
  }, [startSession, stopLiveListening]);

  useEffect(() => {
    getProtocolConfig()
      .then((c) => { if (c?.enabled) return getPublished().then((d) => setPublishedProtocols(d.protocols || [])); })
      .catch(() => {});
  }, []);

  const loadChecklist = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const data = await getSessionChecklist(sid);
      chunkSecondsRef.current = data.chunk_seconds || 15;
      setChecklist(data.items || []);
    } catch {
      setChecklist([]);   // no checklist is a normal state, not an error to shout about
    }
  }, []);

  /**
   * Throw the visit away instead of writing it up — a mis-started recording, the wrong
   * patient, a consent withdrawn. The recording and any draft are deleted server-side;
   * vitals taken during it stay on the patient's record.
   */
  const handleDiscard = async () => {
    if (!sessionId) { navigate('/patients'); return; }
    setDiscarding(true);
    skipUploadRef.current = true;     // a discarded visit's recording is not uploaded
    try {
      // Stop the microphone first, or it keeps running after we navigate away.
      // Release the microphone the same way the unmount cleanup does, or it keeps
      // recording after we navigate away.
      try {
        const mr = mediaRecorderRef.current;
        if (mr && mr.state !== 'inactive') mr.stop();
        mr?.stream?.getTracks().forEach((t) => t.stop());
      } catch (_) { /* already stopped */ }
      try { liveStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) { /* no live stream */ }
      const { data } = await axios.delete(`${API_URL}/api/v1/clinical/session/${sessionId}`);
      toast.success(data?.message || 'Visit discarded');
      navigate('/patients');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not discard this visit');
      skipUploadRef.current = false;
      setDiscarding(false);
      setOpenDiscardDialog(false);
    }
  };

  const handleSelectProtocol = async (pid) => {
    setSelectedProtocol(pid);
    if (!sessionId) return;
    try {
      await attachProtocolToSession(sessionId, pid || null);
      if (pid) toast.success('Protocol attached — adherence will be checked');
    } catch {
      toast.error('Could not attach protocol');
      return;
    }
    if (pid) {
      await loadChecklist(sessionId);
    } else {
      setChecklist([]);
    }
  };

  // Post one clip and apply whatever it answered. Failures here are deliberately
  // quiet: the checklist is an aid, and a flaky network must never distract from
  // the consultation. After three in a row we stop listening and say so.
  const sendClip = useCallback(async (blob, sid) => {
    if (!blob || blob.size < 2048) return;
    try {
      const res = await observeChecklist(sid, blob, lastClipTextRef.current);
      liveFailuresRef.current = 0;
      if (!res.disabled && !res.complete) setAiChecks((n) => n + 1);
      if (res.disabled) {
        stopLiveListening();
        setLiveStatus('paused');
        return;
      }
      lastClipTextRef.current = res.transcript || '';
      const covered = res.covered || [];
      if (covered.length) {
        setChecklist((prev) => prev.map((it) => {
          const hit = covered.find((c) => c.item_id === it.id);
          return hit ? { ...it, covered: true, source: 'ai', evidence: hit.evidence } : it;
        }));
      }
      if (res.complete) {
        stopLiveListening();
        setLiveStatus('complete');
      }
    } catch (e) {
      liveFailuresRef.current += 1;
      if (liveFailuresRef.current >= 3) {
        stopLiveListening();
        setLiveStatus('paused');
      }
    }
  }, [stopLiveListening]);

  // Record a clip, hand it off, and immediately begin the next one. Each clip is
  // a self-contained recording (not a slice of a longer one), so it decodes on
  // its own server-side.
  const cycleClip = useCallback((sid) => {
    if (!liveActiveRef.current) return;
    const stream = liveStreamRef.current;
    if (!stream) return;
    let rec;
    try {
      rec = new MediaRecorder(
        stream,
        MediaRecorder.isTypeSupported('audio/webm') ? { mimeType: 'audio/webm' } : undefined,
      );
    } catch (_) {
      stopLiveListening();
      setLiveStatus('paused');
      return;
    }
    liveRecorderRef.current = rec;
    const parts = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) parts.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(parts, { type: rec.mimeType || 'audio/webm' });
      const send = liveActiveRef.current || liveFlushRef.current;
      liveFlushRef.current = false;
      cycleClip(sid);                       // next clip starts first — no dead air
      if (send) sendClip(blob, sid);
    };
    rec.start();
    liveTimerRef.current = setTimeout(() => {
      try { if (rec.state !== 'inactive') rec.stop(); } catch (_) {}
    }, chunkSecondsRef.current * 1000);
  }, [sendClip, stopLiveListening]);

  const startLiveListening = useCallback((stream, sid) => {
    if (!aiAssistRef.current) return;              // provider is ticking by hand
    if (!checklistRef.current.some((i) => !i.covered)) return;
    try {
      liveStreamRef.current = stream.clone();
    } catch (_) {
      return;                               // no clone, no live checking; recording is unaffected
    }
    liveActiveRef.current = true;
    liveFailuresRef.current = 0;
    lastClipTextRef.current = '';
    setLiveStatus('listening');
    cycleClip(sid);
  }, [cycleClip]);

  // Switching modes takes effect immediately, mid-visit included: turn it off and
  // the listening stops (and the spending with it); turn it on and it picks up
  // from the questions still outstanding.
  const toggleAiAssist = (on) => {
    setAiAssist(on);
    aiAssistRef.current = on;
    localStorage.setItem(AI_ASSIST_KEY, on ? 'on' : 'off');
    if (!on) {
      stopLiveListening();
      setLiveStatus('idle');
      return;
    }
    if (isRecording && !liveActiveRef.current) {
      const stream = mediaRecorderRef.current?.stream;
      if (stream) startLiveListening(stream, sessionId);
    }
  };

  const toggleChecklistItem = async (item) => {
    const next = !item.covered;
    setChecklist((prev) => prev.map((i) => (
      i.id === item.id ? { ...i, covered: next, source: next ? 'manual' : null, evidence: null } : i
    )));
    try {
      await markChecklistItem(sessionId, item.id, next);
    } catch {
      toast.error('Could not update the checklist');
      setChecklist((prev) => prev.map((i) => (i.id === item.id ? item : i)));
      return;
    }
    // Un-ticking mid-visit gives the listener something to look for again.
    if (!next && isRecording && !liveActiveRef.current && liveStatus === 'complete') {
      const stream = mediaRecorderRef.current?.stream;
      if (stream) startLiveListening(stream, sessionId);
    }
  };

  const startRecording = async () => {
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      setMicError(true);
      toast.error('Could not access microphone. Grant permission in your browser, or use Demo mode below.');
      return;
    }
    // Pressing record is what uses one of the clinic's AI sessions, so ask first: a
    // clinic that has run out finds out now, before the consultation, not after it.
    try {
      await axios.post(`${API_URL}/api/v1/clinical/session/${sessionId}/recording`);
    } catch (error) {
      stream.getTracks().forEach((t) => t.stop());
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not start recording', { duration: 7000 });
      return;
    }
    const recorder = new MediaRecorder(stream);
    mediaRecorderRef.current = recorder;
    audioChunksRef.current = [];
    skipUploadRef.current = false;

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) audioChunksRef.current.push(event.data);
    };

    recorder.onstop = async () => {
      // Release the microphone immediately so the browser's mic indicator
      // (red dot) turns off as soon as recording stops.
      try { recorder.stream.getTracks().forEach((t) => t.stop()); } catch (_) {}
      if (skipUploadRef.current) return;
      const mime = recorder.mimeType || 'audio/webm';
      const ext = mime.includes('webm') ? 'webm' : mime.includes('mp4') ? 'm4a' : 'webm';
      pendingAudioRef.current = { blob: new Blob(audioChunksRef.current, { type: mime }), ext };
      setRecordingDone(true);
      const ok = await uploadAudio();
      const next = afterUploadRef.current;
      afterUploadRef.current = null;
      if (next) {
        if (ok) next(); else setIsProcessing(false);
      }
    };

    recorder.start();
    setIsRecording(true);
    startLiveListening(stream, sessionId);
    timerRef.current = setInterval(() => setRecordingTime((prev) => prev + 1), 1000);
  };

  // Stop PAUSES the recorder rather than ending it, so "Continue Recording" carries on
  // in the same recording. Ending it and starting a fresh recorder used to replace
  // everything said before the pause with whatever came after it.
  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && isRecording) {
      if (recorder.state === 'recording') recorder.pause();
      stopLiveListening(true);     // check the final seconds before the dialog's verdict
      setLiveStatus('idle');
      setIsRecording(false);
      clearInterval(timerRef.current);
      setOpenStopDialog(true);
    }
  };

  const continueRecording = () => {
    setOpenStopDialog(false);
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'paused') return;
    recorder.resume();
    setIsRecording(true);
    startLiveListening(recorder.stream, sessionId);
    timerRef.current = setInterval(() => setRecordingTime((prev) => prev + 1), 1000);
  };

  const uploadAudio = async () => {
    const pending = pendingAudioRef.current;
    if (!pending) return false;
    const formData = new FormData();
    formData.append('file', pending.blob, `recording.${pending.ext}`);
    setUploadStatus('uploading');
    try {
      await axios.post(`${API_URL}/api/v1/scribe/session/${sessionId}/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadStatus('done');
      return true;
    } catch (error) {
      setUploadStatus('error');
      const msg = error.response?.data?.detail || error.message;
      toast.error(`Failed to upload audio: ${msg}. Your recording is kept — press Generate Note to try again.`,
        { duration: 8000 });
      return false;
    }
  };

  const runProcessing = async () => {
    setIsProcessing(true);
    try {
      await axios.post(`${API_URL}/api/v1/clinical/notes/${sessionId}/process`);
      toast.success('AI processing complete!');
      navigate(`/review/${sessionId}`);
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      toast.error(`Processing failed: ${msg}`);
      setIsProcessing(false);
    }
  };

  // Generate Note: end the recording, upload it, then write the note — in that order.
  const generateNote = async () => {
    setOpenStopDialog(false);
    setIsProcessing(true);
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      afterUploadRef.current = runProcessing;
      recorder.stop();             // onstop uploads, then runs the note
      return;
    }
    if (uploadStatus !== 'done' && !(await uploadAudio())) {
      setIsProcessing(false);
      return;
    }
    runProcessing();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (isProcessing) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          AI Processing
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {uploadStatus === 'uploading'
            ? 'Uploading your recording...'
            : 'Generating SOAP note from your recording...'}
        </Typography>
        <Box sx={{ maxWidth: 400, mx: 'auto' }}>
          <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
        </Box>
      </Box>
    );
  }

  const liveStatusChip = {
    listening: { label: 'Listening', color: 'error', icon: <ListeningIcon /> },
    complete: { label: 'All asked', color: 'success', icon: <CheckIcon /> },
    paused: { label: 'Live check paused', color: 'default', icon: undefined },
  }[liveStatus];

  const checklistPanel = checklist.length > 0 && (
    <Card sx={{ position: { lg: 'sticky' }, top: 16 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography fontWeight={700}>Questions to ask</Typography>
          {aiAssist
            ? liveStatusChip && (
              <Chip size="small" label={liveStatusChip.label} color={liveStatusChip.color}
                icon={liveStatusChip.icon} variant={liveStatus === 'paused' ? 'outlined' : 'filled'} />
            )
            : <Chip size="small" label="Manual" variant="outlined" />}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {coveredCount} of {checklist.length} asked
        </Typography>
        <LinearProgress
          variant="determinate"
          value={checklist.length ? (coveredCount / checklist.length) * 100 : 0}
          color="success"
          sx={{ height: 6, borderRadius: 3, my: 1.5 }}
        />

        {/* Mode: AI listens along, or the provider ticks by hand. The credit cost
            is stated up front — it's charged to the clinic every visit it's on. */}
        <Box sx={{ p: 1.25, mb: 1.5, borderRadius: 1.5, bgcolor: 'action.hover' }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>AI assistance</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {aiAssist
                  ? `On — listens as you talk and ticks each question off itself. Uses extra AI credits: about ${Math.max(1, Math.round(60 / (chunkSecondsRef.current || 15)))} checks a minute of recording.`
                  : 'Off — tick each question yourself as you ask it. No AI credits used.'}
              </Typography>
              {aiAssist && aiChecks > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {aiChecks} AI check{aiChecks === 1 ? '' : 's'} used this visit
                </Typography>
              )}
            </Box>
            <Switch size="small" checked={aiAssist} onChange={(e) => toggleAiAssist(e.target.checked)} />
          </Stack>
        </Box>

        {aiAssist && liveStatus === 'paused' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Automatic ticking is unavailable — tick questions off yourself. Your recording and note
            are unaffected.
          </Typography>
        )}
        <Stack spacing={1}>
          {checklist.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: 'flex', gap: 1, p: 1, borderRadius: 1.5,
                border: '1px solid',
                borderColor: item.covered ? 'success.light' : 'divider',
                bgcolor: item.covered ? 'rgba(22,163,74,0.08)' : 'transparent',
                transition: 'background-color .3s, border-color .3s',
              }}
            >
              <Tooltip title={item.covered ? 'Mark as not asked' : 'Mark as asked'}>
                <IconButton size="small" sx={{ p: 0.25, alignSelf: 'flex-start' }}
                  onClick={() => toggleChecklistItem(item)}>
                  {item.covered
                    ? <CheckIcon fontSize="small" color="success" />
                    : <PendingIcon fontSize="small" color="disabled" />}
                </IconButton>
              </Tooltip>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: item.covered ? 600 : 400, color: item.covered ? 'success.dark' : 'text.primary' }}
                >
                  {item.text}
                </Typography>
                {!item.covered && item.hint && (
                  <Typography variant="caption" color="text.secondary">{item.hint}</Typography>
                )}
                {item.covered && item.evidence && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    “{item.evidence}”
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {aiAssist
            ? "Ticks are a prompt, not a record — questions turn green a few seconds after they're answered. Correct anything the listener gets wrong by tapping it."
            : 'Tap each question as you ask it. Anything left grey is listed when you stop recording.'}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexDirection: { xs: 'column', lg: 'row' } }}>
    <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/patients')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight={600}>
          Recording Session
        </Typography>
      </Box>

      {patient && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {patient.full_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  DOB: {patient.date_of_birth || '—'} • {patient.reason || 'No visit reason'}
                </Typography>
              </Box>
              <Chip
                label={isRecording ? 'Recording' : 'Ready'}
                color={isRecording ? 'error' : 'success'}
                icon={isRecording ? <MicIcon /> : <CheckIcon />}
              />
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Latest vitals
            </Typography>
            <VitalsSummary
              vitals={patient}
              emptyText="No vitals recorded yet — add them from the patient's Details tab."
            />
          </CardContent>
        </Card>
      )}

      {publishedProtocols.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <ProtocolIcon color="primary" />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography fontWeight={600}>Document against a protocol (optional)</Typography>
              <Typography variant="body2" color="text.secondary">
                {checklist.length > 0
                  ? "Its questions appear on the right and tick off as you ask them."
                  : 'The AI flags how the visit followed it.'}
              </Typography>
            </Box>
            {/* Locked once recording starts: switching protocol would swap the
                checklist out from under the ticks already collected. */}
            <TextField select size="small" label="Protocol" value={selectedProtocol}
              disabled={isRecording}
              helperText={isRecording ? 'Locked while recording' : undefined}
              onChange={(e) => handleSelectProtocol(e.target.value)} sx={{ minWidth: 240 }}>
              <MenuItem value="">None</MenuItem>
              {publishedProtocols.map((p) => <MenuItem key={p.id} value={p.id}>{p.name} ({p.version})</MenuItem>)}
            </TextField>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 3, minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CardContent sx={{ textAlign: 'center' }}>
          {isRecording ? (
            <Box>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h2" fontWeight={700} color="error.main">
                  {formatTime(recordingTime)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Recording in progress...
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <IconButton
                  onClick={stopRecording}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' }
                  }}
                >
                  <StopIcon fontSize="large" />
                </IconButton>
              </Box>
            </Box>
          ) : recordingDone ? (
            <Box>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6">This visit's recording is saved</Typography>
                <Typography variant="body2" color="text.secondary">
                  {uploadStatus === 'error'
                    ? 'It has not reached the server yet — Generate Note tries again.'
                    : 'Generate the note from it, or discard the visit.'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                <Button variant="contained" size="large" onClick={generateNote} sx={{ py: 2, px: 4 }}>
                  Generate Note
                </Button>
                <Button color="error" startIcon={<DeleteIcon />} onClick={() => setOpenDiscardDialog(true)}>
                  Discard visit
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  Ready to record
                </Typography>
                {micError && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                    Allow microphone access in your browser. Recording is required to generate notes.
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {/* Not until the visit exists: the recording is charged against it, and
                    a click in the first moment after the page opens had none yet. */}
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<MicIcon />}
                  onClick={startRecording}
                  disabled={!sessionId}
                  sx={{ py: 2, px: 4 }}
                >
                  Start Recording
                </Button>
                {/* Wrong patient, or opened by mistake — leave without a visit hanging
                    around holding an empty recording. */}
                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setOpenDiscardDialog(true)}
                >
                  Discard visit
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Session Tips
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Speak clearly and at a normal pace
            <br />
            • The AI will automatically identify speakers
            <br />
            • Stop pauses the recording — you can continue it before generating the note
            <br />
            • Click the transcript segments to hear playback
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={openStopDialog} onClose={continueRecording}>
        <DialogTitle>Stop Recording?</DialogTitle>
        <DialogContent>
          <Typography>
            Recording time: {formatTime(recordingTime)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The AI will generate a SOAP note from this recording. You can review and edit it before finalizing.
          </Typography>
          {pendingCount > 0 && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {pendingCount} protocol question{pendingCount === 1 ? '' : 's'} not marked as asked
              </Typography>
              {checklist.filter((i) => !i.covered).map((i) => (
                <Typography key={i.id} variant="caption" sx={{ display: 'block' }}>• {i.text}</Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          {/* Discard sits apart from the other two on purpose: it is destructive, and
              it should not sit next to the button you press by habit. */}
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => { setOpenStopDialog(false); setOpenDiscardDialog(true); }}
          >
            Discard visit
          </Button>
          <Box>
            <Button onClick={continueRecording}>Continue Recording</Button>
            <Button variant="contained" onClick={generateNote} sx={{ ml: 1 }}>
              Generate Note
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={openDiscardDialog} onClose={() => !discarding && setOpenDiscardDialog(false)}>
        <DialogTitle>Discard this visit?</DialogTitle>
        <DialogContent>
          <Typography>
            The recording will be <strong>permanently deleted</strong>, along with any
            note drafted from it. This cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Any vitals recorded during this visit stay on the patient's record — only the
            visit itself is thrown away.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDiscardDialog(false)} disabled={discarding}>
            Keep it
          </Button>
          <Button color="error" variant="contained" onClick={handleDiscard} disabled={discarding}>
            {discarding ? 'Discarding…' : 'Discard visit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>

    {checklistPanel && (
      <Box sx={{ width: { xs: '100%', lg: 360 }, flexShrink: 0 }}>
        {checklistPanel}
      </Box>
    )}
    </Box>
  );
}
